import { useEffect, useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { IMAGES } from "../data/content";
import { useSiteNav } from "../hooks/useSiteNav";

const LINES = ["POWERING THE", "INFRASTRUCTURE", "BEHIND INDIA"];

export const Hero = () => {
    const onNavigate = useSiteNav();
    const ref = useRef(null);
    const videoRef = useRef(null);
    const reduce = useReducedMotion();

    useEffect(() => {
        if (reduce && videoRef.current) videoRef.current.pause();
    }, [reduce]);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[100svh] flex items-end overflow-hidden bg-vec-charcoal"
            aria-label="Introduction"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]" aria-hidden="true">
                <video
                    ref={videoRef}
                    data-testid="hero-background-video"
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={IMAGES.hero}
                >
                    <source src="/hero-bg.webm" type="video/webm" />
                    <source src="/hero-bg.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_85%_62%_at_0%_100%,rgba(23,34,28,0.68),transparent_68%)]" aria-hidden="true" />
                <div className="absolute inset-0 tech-grid-dark opacity-60" />
            </motion.div>

            <div className="absolute top-24 right-4 sm:right-8 hidden md:flex flex-col items-end gap-1 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/45 z-10">
                <span>PWR / INFRA / EXEC</span>
                <span>LAT 17.68° N — LON 74.00° E</span>
            </div>

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 w-full vec-container pb-16 sm:pb-24 pt-36"
            >
                <div className="flex items-center gap-3 font-mono-tech text-[11px] sm:text-xs uppercase tracking-[0.24em] text-vec-green-bright mb-6 sm:mb-7 [text-shadow:0_1px_14px_rgba(10,18,14,0.6)]">
                    <span className="h-px w-10 bg-vec-green-bright/70" aria-hidden="true" />
                    Power Infrastructure • Engineering • Execution
                </div>

                <h1
                    data-testid="hero-headline"
                    className="font-display font-bold uppercase tracking-tight text-white text-[clamp(2.65rem,1.7rem+3.4vw,6.2rem)] leading-[1.03] [text-shadow:0_2px_28px_rgba(10,18,14,0.55)]"
                >
                    {LINES.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={reduce ? false : { y: "110%" }}
                                animate={{ y: "0%" }}
                                transition={{
                                    duration: 0.8,
                                    delay: 0.25 + i * 0.13,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                            >
                                {i === 1 ? (
                                    <span className="text-vec-green-bright">{line}</span>
                                ) : (
                                    line
                                )}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={reduce ? false : { opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.75 }}
                    className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/90 [text-shadow:0_1px_16px_rgba(10,18,14,0.6)]"
                >
                    Engineering and execution across power transmission, substations, renewable
                    energy and associated infrastructure.
                </motion.p>

                <motion.div
                    initial={reduce ? false : { opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.9 }}
                    className="mt-8 sm:mt-9 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-7"
                >
                    <button
                        type="button"
                        data-testid="hero-explore-capabilities-button"
                        onClick={() => onNavigate("#capabilities")}
                        className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-vec-green hover:bg-vec-green-bright text-white font-semibold text-sm sm:text-[15px] tracking-wide px-8 py-4 transition-colors duration-300"
                    >
                        Explore Capabilities
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        data-testid="hero-get-in-touch-button"
                        onClick={() => onNavigate("#contact")}
                        className="inline-flex items-center justify-center gap-2 py-2 text-white/75 hover:text-vec-green-bright font-medium text-sm tracking-wide underline decoration-white/30 underline-offset-8 hover:decoration-vec-green-bright transition-colors duration-300"
                    >
                        Contact Us
                    </button>
                </motion.div>
            </motion.div>

            <motion.button
                type="button"
                data-testid="hero-scroll-indicator"
                onClick={() => onNavigate("#about")}
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.6 }}
                className="absolute bottom-6 right-6 sm:right-10 z-10 hidden sm:flex flex-col items-center gap-2 text-white/60 hover:text-vec-green-bright transition-colors"
                aria-label="Scroll to about section"
            >
                <span className="font-mono-tech text-[9px] uppercase tracking-[0.3em]">Scroll</span>
                <span className="block w-px h-10 bg-white/25 overflow-hidden">
                    <span className="block w-full h-full bg-vec-green-bright animate-scroll-line" />
                </span>
                <ArrowDown size={12} aria-hidden="true" />
            </motion.button>
        </section>
    );
};
